import i18n from "i18next";
import { errorDetails } from "../Utils/errorDetails";

import { FRAME_TYPE } from "./webcodecs/constants";

import type { IProvider } from "./Streaming";

import type { EncodersConfig } from "./webcodecs/config";
import { storedLocalApiPort } from "../lib/localApi";

export interface WebTransportConfig {
  width: number;

  height: number;

  encoders: EncodersConfig;

  recording: {
    enabled: boolean;

    basePath?: string;

    /** Preferencia del usuario: grabar el VOD del live en la nube
     * (streaming-server Rust acepta `record` en stream-start). */

    record?: boolean;
  };

  providers: IProvider[];

  /** Transporte LOCAL por SHM: si "local_shm", kero lee el bitstream de video directo del ring SHM
   * (no por WebTransport). Solo se setea en broadcast local; ausente en cloud/selfhost. */

  source?: string;

  /** Nombre del ring SHM que escribe el addon NVENC (solo con source="local_shm"). */

  ringName?: string;

  /** Capacidad (bytes) del ring SHM de video (solo con source="local_shm"). */

  ringCap?: number;

  /** Nombre del ring SHM de AUDIO (Etapa 2: audio PCM por SHM en vez de WebTransport). */

  audioRingName?: string;

  /** Capacidad (bytes) del ring SHM de audio. */

  audioRingCap?: number;
}

export interface SelfHostConfig {
  id: string;

  host: string;

  username: string;

  password: string;

  timestamp: number;
}

export interface WebTransportSelfHostedConfig {
  selfhost_config: SelfHostConfig;

  providers: IProvider[];

  width: number;

  height: number;

  encoders: EncodersConfig;

  recording: {
    enabled: boolean;

    basePath?: string;

    /** Preferencia del usuario: grabar el VOD del live en la nube. */

    record?: boolean;
  };
}

interface ReconnectStatus {
  source: string;

  attempt: number;

  maxAttempts: number;

  status: "reconnecting" | "reconnected" | "failed";
}

/** Salud periódica del encoder del broadcast (Kero datagram 0x08, Fase 5.1). */

export interface BroadcastStats {
  avgBitrateKbps?: number;

  droppedFrames?: number;

  renditions?: number;

  /** Round-trip time in milliseconds (network latency indicator). */

  rttMs?: number;
}

/** Candidatos de conexión al Kero local, en orden de preferencia. */

const HOSTNAME_URL = "https://stream.eriol.io:8443/stream";

const LOOPBACK_URL = "https://127.0.0.1:8443/stream";

const CANDIDATE_URLS = [HOSTNAME_URL, LOOPBACK_URL];

/** Cota propia por candidato. Sin esto, un resolver que secuestra el NXDOMAIN
 * (práctica común de ISPs residenciales y captive portals) hace que el hostname
 * resuelva a una IP ajena que no responde QUIC, y el intento queda colgado hasta
 * el timeout de handshake de Chromium (~10s) antes de probar el candidato
 * siguiente. */

const ATTEMPT_TIMEOUT_MS = 3000;

export class WebTransportManager {
  private transport: WebTransport | null = null;

  /** Última URL que conectó OK: en reconexiones se prueba primero, para no
   * re-pagar un candidato muerto (hostname sin el switch del shell, o detrás de
   * un proxy corporativo donde WebTransport exige DIRECT). */

  private lastGoodUrl: string | null = null;

  /** URLs ya advertidas: el warn de un candidato fallido se emite una vez por
   * sesión (en flotas sin el switch, el hostname falla en CADA connect y esto
   * era ruido permanente de consola en un camino que termina conectando). */

  private readonly warnedUrls = new Set<string>();

  private onClosedCallback: (() => void) | null = null;

  private onServerErrorCallback: ((message: string) => void) | null = null;

  private onConnectionLostCallback: ((reason: string) => void) | null = null;

  private onReconnectStatusCallback:
    | ((status: ReconnectStatus) => void)
    | null = null;

  private onBroadcastStatsCallback: ((stats: BroadcastStats) => void) | null =
    null;

  private readyListeners: Set<() => void> = new Set();

  private connecting: Promise<void> | null = null;

  private datagramReaderAbort: AbortController | null = null;

  constructor() {}

  setOnClosed(callback: () => void) {
    this.onClosedCallback = callback;
  }

  setOnServerError(callback: ((message: string) => void) | null) {
    this.onServerErrorCallback = callback;
  }

  setOnConnectionLost(callback: ((reason: string) => void) | null) {
    this.onConnectionLostCallback = callback;
  }

  setOnReconnectStatus(callback: ((status: ReconnectStatus) => void) | null) {
    this.onReconnectStatusCallback = callback;
  }

  setOnBroadcastStats(callback: ((stats: BroadcastStats) => void) | null) {
    this.onBroadcastStatsCallback = callback;
  }

  isConnected(): boolean {
    return this.transport !== null;
  }

  /**
   * Subscribe to the "ready" event. Fires immediately if already connected.
   * Returns an unsubscribe function.
   */

  onReady(callback: () => void): () => void {
    if (this.transport) {
      callback();

      return () => {};
    }

    this.readyListeners.add(callback);

    return () => {
      this.readyListeners.delete(callback);
    };
  }

  /**
   * Wait until WebTransport is connected. Retries connect() up to maxRetries.
   */

  async ensureConnected(maxRetries = 10, intervalMs = 2000): Promise<void> {
    if (this.transport) return;

    for (let i = 0; i < maxRetries; i++) {
      try {
        await this.connect();

        return;
      } catch {
        if (i < maxRetries - 1) {
          await new Promise((r) => setTimeout(r, intervalMs));
        }
      }
    }

    throw new Error("Kero not available after retries");
  }

  getTransport(): WebTransport | null {
    return this.transport;
  }

  async connect(): Promise<void> {
    // Deduplicate concurrent connect() calls

    if (this.connecting) return this.connecting;

    this.connecting = this._doConnect();

    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  /** Opciones de pinning: hash sha-256 del cert de Kero, servido por /services
   * (que lo recalcula del archivo en cada request — sigue rotaciones en vivo).
   * Sin apiPort o ante cualquier fallo, se conecta sin pinning (el shell corre
   * con ignore-certificate-errors). */

  private async fetchPinningOptions(): Promise<WebTransportOptions> {
    // Puerto entero estricto (lo valida el helper): un valor arbitrario en
    // localStorage interpolado en la URL podría redirigir el fetch a otro host.
    const apiPort = storedLocalApiPort();
    if (!apiPort) return {};

    try {
      // Timeout propio: sin esto, un server local colgado bloquea el connect

      // ANTES de que corran los timeouts por candidato.

      const response = await fetch(`http://localhost:${apiPort}/services`, {
        signal: AbortSignal.timeout(2_000),
      });

      if (!response.ok) return {};

      const data = await response.json();

      const hashHex: unknown = data.certificate?.hash;

      // sha-256 exacto (32 bytes = 64 hex): un hash malformado, truncado o de

      // otro algoritmo no debe tirar en el connect ni armar un pin imposible de

      // matchear — se sigue sin pinning, igual que sin hash.

      if (typeof hashHex !== "string" || !/^[0-9a-f]{64}$/i.test(hashHex)) {
        return {};
      }

      const hashBuffer = new Uint8Array(
        hashHex.match(/.{2}/g)!.map((byte: string) => parseInt(byte, 16)),
      );

      return {
        serverCertificateHashes: [
          {
            algorithm: "sha-256",

            value: hashBuffer,
          },
        ],
      };
    } catch (e) {
      console.warn(
        "⚠️ Could not fetch certificate info, proceeding without hash pinning:",

        errorDetails(e),
      );

      return {};
    }
  }

  /** Intenta un candidato con timeout propio; si no llegó a ready, lo cierra. */

  private async attemptConnect(
    url: string,

    options: WebTransportOptions,
  ): Promise<WebTransport> {
    const attempt = new WebTransport(url, options);

    // Un intento descartado también rechaza su `closed`: catch no-op para que

    // nunca burbujee como unhandledrejection (el ganador re-atacha handlers

    // reales en _doConnect; múltiples consumidores del mismo promise conviven).

    attempt.closed.catch(() => {});

    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
      await Promise.race([
        attempt.ready,

        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error(`timeout tras ${ATTEMPT_TIMEOUT_MS}ms`)),
            ATTEMPT_TIMEOUT_MS,
          );
        }),
      ]);

      return attempt;
    } catch (e) {
      try {
        attempt.close();
      } catch {
        // ya cerrado/abortado
      }

      throw e;
    } finally {
      clearTimeout(timer);
    }
  }

  private async _doConnect(): Promise<void> {
    // Kero levanta el server WebTransport en loopback. La URL canónica es

    // `stream.eriol.io`: el shell de Electron la resuelve a 127.0.0.1 con el switch

    // `host-resolver-rules` (extension/src/main.ts) — mismo efecto que el viejo hosts

    // entry pero sin UAC ni admin. El fallback a 127.0.0.1 directo cubre shells sin

    // ese switch (≤0.8.7 sin hosts entry), dev en un browser pelado, y máquinas con

    // proxy de sistema/PAC (WebTransport exige DIRECT y el bypass implícito de proxy

    // cubre 127.0.0.1 por host literal, pero NUNCA a stream.eriol.io — la decisión de

    // proxy corre antes que host-resolver-rules, así que ahí el hostname muere rápido

    // con ERR_TUNNEL_CONNECTION_FAILED y cae a la IP). El cert de Kero matchea en

    // ambos casos (SAN DnsName stream.eriol.io + IpAddress 127.0.0.1) y el pinning

    // por hash (serverCertificateHashes) ignora el hostname.

    try {
      const transportOptions = await this.fetchPinningOptions();

      const pinned =
        (transportOptions.serverCertificateHashes?.length ?? 0) > 0;

      // Sin pinning no hay verificación real del peer (el shell corre con

      // ignore-certificate-errors): limitarse a loopback, que no es secuestrable

      // por DNS. Con pinning, hostname primero (el pin vuelve inocuo un DNS

      // pisado en shells sin el switch) y la última URL buena adelante.

      const candidateUrls = !pinned
        ? [LOOPBACK_URL]
        : this.lastGoodUrl
          ? [
              this.lastGoodUrl,

              ...CANDIDATE_URLS.filter((u) => u !== this.lastGoodUrl),
            ]
          : [...CANDIDATE_URLS];

      // Intento en orden (última URL buena primero). `this.transport` se asigna

      // recién con la sesión establecida — un intento fallido no deja al manager

      // en estado "conectado" a medias.

      let transport: WebTransport | null = null;

      let lastError: unknown = null;

      for (const url of candidateUrls) {
        try {
          transport = await this.attemptConnect(url, transportOptions);

          this.lastGoodUrl = url;

          break;
        } catch (e) {
          lastError = e;

          if (!this.warnedUrls.has(url)) {
            this.warnedUrls.add(url);

            console.warn(`⚠️ WebTransport connect failed for ${url}:`, errorDetails(e));
          }
        }
      }

      if (!transport) {
        // Normalizar a Error: lastError puede ser cualquier valor rechazado

        // (DOMException, string) y aguas arriba se espera un Error real.

        if (lastError instanceof Error) throw lastError;

        throw new Error(
          lastError == null
            ? "WebTransport connection failed"
            : String(lastError),
        );
      }

      this.transport = transport;

      // Start reading incoming datagrams for server errors

      this.startDatagramReader();

      // Notify ready listeners

      this.readyListeners.forEach((cb) => cb());

      this.readyListeners.clear();

      this.transport.closed

        .then(() => {
          console.log("🔌 WebTransport closed cleanly");

          this.close();
        })

        .catch((e) => {
          console.error("🔌 WebTransport closed abruptly:", errorDetails(e));

          this.onConnectionLostCallback?.(
            e instanceof Error ? e.message : "Connection lost unexpectedly",
          );

          this.close();
        });
    } catch (e) {
      console.error("🔌 WebTransport connection failed:", errorDetails(e));

      throw e;
    }
  }

  async sendConfig(config: WebTransportConfig): Promise<boolean> {
    if (!this.transport) return false;

    const configData = {
      type: "config",

      width: config.width,

      height: config.height,

      encoders: config.encoders,

      recording: config.recording,

      providers: config.providers

        .filter((p) => p.enabled)

        .map((p) => ({
          name: p.name,

          endpoint: p.endpoint,

          key: p.key,

          // Override de calidad por destino (rendition). Se omite la key cuando no hay

          // override — el server trata la ausencia como "usar el encoding maestro".

          ...(p.encoding ? { encoding: p.encoding } : {}),
        })),

      // Transporte LOCAL por SHM (solo si el caller lo setea): kero lee video (y audio, Etapa 2) del

      // ring directo. audioRingName presente → kero abre el 2º lector de audio.

      ...(config.source === "local_shm"
        ? {
            source: config.source,

            ringName: config.ringName,

            ringCap: config.ringCap,

            audioRingName: config.audioRingName,

            audioRingCap: config.audioRingCap,
          }
        : {}),
    };

    try {
      const bidiStream = await this.transport.createBidirectionalStream();

      const writer = bidiStream.writable.getWriter();

      const reader = bidiStream.readable.getReader();

      await writer.write(new TextEncoder().encode(JSON.stringify(configData)));

      await writer.close();

      const decoder = new TextDecoder();

      const timeoutPromise = new Promise<{ done: true; value: undefined }>(
        (resolve) =>
          setTimeout(() => resolve({ done: true, value: undefined }), 10000),
      );

      const readPromise = reader.read();

      const result = await Promise.race([readPromise, timeoutPromise]);

      if (result.done || !result.value) {
        reader.releaseLock();

        throw new Error("Kero did not respond to pipeline configuration");
      }

      const response = decoder.decode(result.value);

      reader.releaseLock();

      try {
        const json = JSON.parse(response);

        if (json.status === "ready") {
          return true;
        } else if (json.error) {
          throw new Error(json.error);
        }
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message !== response) {
          throw parseErr;
        }

        console.warn("⚠️ Could not parse response, proceeding");
      }

      return true;
    } catch (e) {
      console.error("Error sending config:", errorDetails(e));

      throw e;
    }
  }

  async sendSelfhostedConfig(
    config: WebTransportSelfHostedConfig,
  ): Promise<boolean> {
    if (!this.transport) return false;

    const configData = {
      type: "stream_start",

      target: {
        kind: "selfhost_srt",

        server: {
          id: config.selfhost_config.id,

          baseUrl: config.selfhost_config.host,

          username: config.selfhost_config.username,

          password: config.selfhost_config.password,
        },
      },

      width: config.width,

      height: config.height,

      encoders: config.encoders,

      recording: config.recording,

      providers: config.providers

        .filter((p) => p.enabled)

        .map((p) => ({
          name: p.name,

          endpoint: p.endpoint,

          key: p.key,

          // Override de calidad por destino (rendition). Omitida cuando no hay override.

          ...(p.encoding ? { encoding: p.encoding } : {}),
        })),
    };

    try {
      // Use bidirectional stream to wait for session established confirmation

      const bidiStream = await this.transport.createBidirectionalStream();

      const writer = bidiStream.writable.getWriter();

      const reader = bidiStream.readable.getReader();

      // Send config

      await writer.write(new TextEncoder().encode(JSON.stringify(configData)));

      await writer.close();

      console.log("📤 Sent self-hosted config, waiting for confirmation...");

      // Wait for response with timeout

      const decoder = new TextDecoder();

      const timeoutPromise = new Promise<{ done: true; value: undefined }>(
        (resolve) =>
          setTimeout(() => resolve({ done: true, value: undefined }), 10000),
      );

      const readPromise = reader.read();

      const result = await Promise.race([readPromise, timeoutPromise]);

      if (result.done || !result.value) {
        reader.releaseLock();

        throw new Error("Kero did not respond to self-hosted configuration");
      }

      const response = decoder.decode(result.value);

      console.log("📥 Backend response:", response);

      reader.releaseLock();

      try {
        const json = JSON.parse(response);

        if (
          json.status === "ready" ||
          json.status === "selfhost-streaming" ||
          json.status === "selfhost-srt-streaming"
        ) {
          console.log("✅ Self-hosted SRT streaming session established");

          return true;
        } else if (json.error) {
          // Parse self-host error codes from Kero

          const errorMsg = json.error;

          if (errorMsg.includes("SELFHOST_DNS_ERROR")) {
            const translatedError = i18n.t(
              "SELFHOST_ERRORS.SELFHOST_DNS_ERROR",
            );

            throw new Error(translatedError);
          }

          if (errorMsg.includes("SELFHOST_CONNECTION_REFUSED")) {
            const translatedError = i18n.t(
              "SELFHOST_ERRORS.SELFHOST_CONNECTION_REFUSED",
            );

            throw new Error(translatedError);
          }

          if (errorMsg.includes("SELFHOST_TIMEOUT")) {
            const translatedError = i18n.t("SELFHOST_ERRORS.SELFHOST_TIMEOUT");

            throw new Error(translatedError);
          }

          if (errorMsg.includes("SELFHOST_ERROR")) {
            const translatedError = i18n.t("SELFHOST_ERRORS.SELFHOST_ERROR");

            throw new Error(translatedError);
          }

          throw new Error(errorMsg);
        }
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message !== response) {
          throw parseErr;
        }

        console.warn("⚠️ Could not parse response, proceeding");
      }

      return true;
    } catch (e) {
      console.error("Error sending self-hosted config:", errorDetails(e));

      throw e;
    }
  }

  async updateScreenshotPath(path: string) {
    if (!this.transport) {
      console.warn("Cannot update screenshot path: WebTransport not connected");

      return;
    }

    try {
      const encoder = new TextEncoder();

      const pathBytes = encoder.encode(path);

      const datagram = new Uint8Array(1 + pathBytes.length);

      datagram[0] = FRAME_TYPE.SCREENSHOT_CONFIG;

      datagram.set(pathBytes, 1);

      const writer = this.transport.datagrams.writable.getWriter();

      await writer.write(datagram);

      writer.releaseLock();

      console.log("📸 Screenshot path updated:", path);
    } catch (e) {
      console.error("Failed to update screenshot path:", errorDetails(e));
    }
  }

  private startDatagramReader() {
    if (!this.transport) return;

    this.datagramReaderAbort = new AbortController();

    const reader = this.transport.datagrams.readable.getReader();

    const read = async () => {
      try {
        while (true) {
          const { value, done } = await reader.read();

          if (done) break;

          if (!value || value.length < 2) continue;

          const frameType = value[0];

          if (frameType === FRAME_TYPE.SERVER_ERROR) {
            const message = new TextDecoder().decode(value.slice(1));

            console.error("Server error received:", message);

            this.onServerErrorCallback?.(message);
          } else if (frameType === FRAME_TYPE.RECONNECT_STATUS) {
            const message = new TextDecoder().decode(value.slice(1));

            console.log("Reconnect status received:", message);

            try {
              const status = JSON.parse(message) as ReconnectStatus;

              this.onReconnectStatusCallback?.(status);
            } catch {
              // Ignore invalid JSON
            }
          } else if (frameType === FRAME_TYPE.BROADCAST_STATS) {
            try {
              const stats = JSON.parse(
                new TextDecoder().decode(value.slice(1)),
              ) as BroadcastStats;

              this.onBroadcastStatsCallback?.(stats);
            } catch {
              // Ignore invalid JSON
            }
          }
        }
      } catch {
        // Reader cancelled on close — expected
      }
    };

    read();
  }

  close() {
    try {
      this.datagramReaderAbort?.abort();

      this.datagramReaderAbort = null;

      if (this.transport) {
        try {
          this.transport.close();
        } catch (e) {
          console.warn("Error while closing WebTransport:", errorDetails(e));
        }

        this.transport = null;
      }

      if (this.onClosedCallback) {
        this.onClosedCallback();
      }
    } catch (e) {
      console.error("Error during WebTransport close:", errorDetails(e));
    }
  }
}

export const webTransportManager = new WebTransportManager();
